import { Content, Header, Page, Progress } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type {
  ControlPlaneCapability,
  PairingPurpose,
  ControlPlaneOperationalProjection,
} from '@internal/spassu-code-contract';
import {
  type ControlPlaneOnboardingView,
  useControlPlaneOnboarding,
} from '../hooks/useControlPlaneOnboarding';
import {
  DENIED_CAPABILITY,
  useControlPlaneManagement,
  type ControlPlaneManagementView,
} from '../hooks/useControlPlaneManagement';
import { useControlPlaneSkillPlan } from '../hooks/useControlPlaneSkillPlan';
import { activityCockpitRouteRef } from '../routes';
import {
  ConnectionSetupPanel,
  PairingPanel,
} from './ControlPlaneConnectionPanels';
import {
  CapabilityButton,
  ManagementStatePanel,
  CONNECTION_LABELS,
  EXPIRY_BAND_LABELS,
} from './ControlPlaneManagementPanels';
import { ControlPlaneCancelDialog } from './ControlPlaneCancelDialog';
import { ControlPlaneUsageRecoveryPanel } from './ControlPlaneUsageRecoveryPanel';
import { ControlPlaneSkillPlanPanel } from './ControlPlaneSkillPlanPanel';

const useStyles = makeStyles(theme => ({
  layout: {
    display: 'grid',
    gap: theme.spacing(3),
    maxWidth: 1040,
  },
  section: {
    display: 'grid',
    gap: theme.spacing(2),
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
}));

type Confirmation = 'revoke' | null;

function ConfirmationDialog(props: {
  action: Confirmation;
  busy: boolean;
  onClose(): void;
  onConfirm(): void;
}) {
  return (
    <Dialog open={props.action !== null} onClose={props.onClose}>
      <DialogTitle>Revogar credencial?</DialogTitle>
      <DialogContent>
        <Typography>
          A credencial atual deixará de acessar esta atividade. Esta ação fica
          registrada na auditoria permanente.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} disabled={props.busy}>
          Cancelar
        </Button>
        <Button
          color="secondary"
          variant="contained"
          onClick={props.onConfirm}
          disabled={props.busy}
        >
          Confirmar revogação
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SessionActions(props: {
  view: ControlPlaneOnboardingView;
  confirmation: Confirmation;
  setConfirmation(value: Confirmation): void;
}) {
  const classes = useStyles();
  if (!props.view.session) return null;
  const terminal = ['expired', 'revoked', 'completed'].includes(
    props.view.session.status,
  );
  const busy = props.view.operation !== 'idle';
  const confirm = () => {
    if (!props.view.session) return;
    if (props.confirmation === 'revoke') {
      void props.view.revoke(props.view.session.id);
    }
    props.setConfirmation(null);
  };
  return (
    <Box className={classes.row}>
      {!terminal && (
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => props.setConfirmation('revoke')}
          disabled={busy}
        >
          Revogar credencial
        </Button>
      )}
      <ConfirmationDialog
        action={props.confirmation}
        busy={busy}
        onClose={() => props.setConfirmation(null)}
        onConfirm={confirm}
      />
    </Box>
  );
}

/**
 * The canonical `pair` capability decides, not the legacy session status.
 *
 * Terminalization revokes the open session, so a status-only rule reported
 * "revoked, therefore pairable" and the page offered an ENABLED reconnect flow on
 * an activity the backend refuses with ACTIVITY_TERMINAL. The projection is the
 * only surface that knows the activity cycle (ADR-006/ADR-009); the session status
 * narrows it further, because a connected session is not pairable either.
 */
const PAIRING_INTENT_COPY: Partial<
  Record<PairingPurpose, { title: string; body: string; confirm: string }>
> = {
  renewal: {
    title: 'Renovar a conexão desta atividade?',
    body:
      'Um código de uso único será gerado agora. O MESMO workspace local é ' +
      'reutilizado — nada é clonado de novo. A credencial atual continua ' +
      'válida até a nova ser confirmada no terminal; se a validação falhar, ' +
      'nada é revogado.',
    confirm: 'Gerar código de renovação',
  },
  recovery: {
    title: 'Recuperar a conexão desta atividade?',
    body:
      'Um código de uso único será gerado agora. O MESMO workspace local é ' +
      'reutilizado — nada é clonado de novo. A atividade e o histórico são ' +
      'preservados.',
    confirm: 'Gerar código de recuperação',
  },
  'cleanup-enrollment': {
    title: 'Atualizar o agente para concluir a exclusão?',
    body:
      'Um código de uso único instala apenas a credencial de limpeza no ' +
      'workspace existente. Nenhum acesso operacional é devolvido a esta ' +
      'atividade encerrada.',
    confirm: 'Gerar código de limpeza',
  },
};

/** Confirms the intent before any one-use pairing material is emitted. */
function PairingIntentDialog(props: {
  purpose: PairingPurpose | null;
  busy: boolean;
  onClose(): void;
  onConfirm(purpose: PairingPurpose): void;
}) {
  const copy = props.purpose ? PAIRING_INTENT_COPY[props.purpose] : undefined;
  return (
    <Dialog
      open={Boolean(copy)}
      onClose={props.onClose}
      aria-labelledby="pairing-intent-title"
    >
      <DialogTitle id="pairing-intent-title">{copy?.title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{copy?.body}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} disabled={props.busy}>
          Voltar
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={props.busy}
          onClick={() => props.purpose && props.onConfirm(props.purpose)}
        >
          {copy?.confirm}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ResumeAttemptDialog(props: {
  open: boolean;
  busy: boolean;
  onClose(): void;
  onConfirm(): void;
}) {
  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      aria-labelledby="resume-attempt-title"
    >
      <DialogTitle id="resume-attempt-title">Retomar conexão?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Uma nova autorização de uso único será gerada para continuar no mesmo
          workspace. O propósito da tentativa será preservado pelo backend.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} disabled={props.busy}>
          Cancelar
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={props.onConfirm}
          disabled={props.busy}
        >
          Confirmar retomada
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Fails CLOSED. The previous shape only refused when the projection had already
 * loaded AND said no, so before the first load, on a failed refresh, or on a host
 * without the API, it fell through to the legacy session check and offered the
 * initial pairing flow again — on a terminal activity too, which is exactly the
 * ADR-006 violation the capability exists to prevent. Emitting one-use credential
 * material needs an affirmative canonical answer, not the absence of a refusal.
 */
function canGeneratePairing(
  view: ControlPlaneOnboardingView,
  pair?: ControlPlaneCapability,
): boolean {
  if (!pair?.allowed) return false;
  return (
    !view.session ||
    ['expired', 'revoked', 'failed'].includes(view.session.status)
  );
}

/**
 * Steady-state live-region text, from the canonical projection only. Transient
 * operation feedback (generating a code, a pairing error) still comes from the
 * hook and takes precedence while it is present. Without a projection it
 * states that the state is unavailable rather than falling back to a
 * handoff-derived claim: asserting "Conectado" from `handoff_state` is exactly
 * what ADR-009 forbids, and it contradicted the panel next to it.
 */
function canonicalAnnouncement(
  projection: ControlPlaneOperationalProjection | null | undefined,
): string {
  if (!projection) return 'Estado da conexão indisponível.';
  const band =
    projection.connection.expiryBand === 'none'
      ? ''
      : ` ${EXPIRY_BAND_LABELS[projection.connection.expiryBand]}.`;
  return `Conexão: ${CONNECTION_LABELS[projection.connection.state]}.${band}`;
}

function ConnectionCard({
  view,
  pairCapability,
  announcement,
}: {
  view: ControlPlaneOnboardingView;
  pairCapability?: ControlPlaneCapability;
  /**
   * Canonical live-region text. `view.announcement` is derived from
   * `handoff_state` with a session fallback and never reads expiry, so on an
   * expired-but-persisted-connected activity it announced "Estado do handoff:
   * Conectado." right beside the projection's "Expirada" — the double truth
   * ADR-009 forbids and BUG-20260819-fallback-legado-afirma-conectado declares
   * fixed. Removing the legacy chip left this second instance behind.
   */
  announcement: string;
}) {
  const classes = useStyles();
  const [confirmation, setConfirmation] = useState<Confirmation>(null);
  if (view.loading) return <Progress />;
  if (!view.activity) {
    return (
      <Card variant="outlined">
        <CardContent className={classes.section}>
          <Typography role="alert" color="error">
            {view.error ?? 'Não foi possível carregar a atividade.'}
          </Typography>
          <Typography role="status" aria-live="polite" aria-atomic="true">
            {view.announcement}
          </Typography>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card variant="outlined">
      <CardContent className={classes.section}>
        {canGeneratePairing(view, pairCapability) && (
          <ConnectionSetupPanel
            busy={view.operation === 'pairing'}
            onGenerate={view.generate}
          />
        )}
        <PairingPanel view={view} />
        <SessionActions
          view={view}
          confirmation={confirmation}
          setConfirmation={setConfirmation}
        />
        {view.error && (
          <Typography role="alert" color="error">
            {view.error}
          </Typography>
        )}
        <Typography role="status" aria-live="polite" aria-atomic="true">
          {announcement}
        </Typography>
      </CardContent>
    </Card>
  );
}

/**
 * Capability-driven actions. Every action is rendered for every authenticated
 * reader; the server decides `allowed` and supplies the reason (ADR-006). Pairing
 * material is only ever produced by the owner's own mutation response and is
 * never persisted here.
 */
function ManagementActions(props: {
  management: ControlPlaneManagementView;
  onboarding: ControlPlaneOnboardingView;
  activityTitle: string;
}) {
  const classes = useStyles();
  const [cancelOpen, setCancelOpen] = useState(false);
  const capabilities = props.management.projection?.capabilities;
  const busy =
    props.management.operation !== 'idle' ||
    props.onboarding.operation !== 'idle';
  // Confirmation before emitting one-use material. A single click used to issue a
  // pairing code immediately, so a mis-click burned the code and — for renewal —
  // started a cutover the developer had not asked for. The dialog also states that
  // the SAME workspace is reused, which is the part users need to know before
  // accepting (ADR-010, US-004/US-005).
  const [pairingIntent, setPairingIntent] = useState<PairingPurpose | null>(
    null,
  );
  const [resumeOpen, setResumeOpen] = useState(false);
  const pair = (purpose: PairingPurpose) => () => setPairingIntent(purpose);
  return (
    <Box className={classes.section} data-testid="management-actions">
      <Box className={classes.row}>
        <CapabilityButton
          capability={capabilities?.renew ?? DENIED_CAPABILITY}
          label="Renovar conexão"
          busy={busy}
          onClick={pair('renewal')}
        />
        <CapabilityButton
          capability={capabilities?.recover ?? DENIED_CAPABILITY}
          label="Recuperar conexão"
          busy={busy}
          onClick={pair('recovery')}
        />
        <CapabilityButton
          capability={capabilities?.resume ?? DENIED_CAPABILITY}
          label="Retomar conexão"
          busy={busy}
          onClick={() => setResumeOpen(true)}
        />
      </Box>
      <Box className={classes.row}>
        {/*
          ADR-016: offered only when the projection says this owner may enroll —
          terminal activity, pending workspace cleanup, no active control
          credential. A global administrator sees the reason, never the code.
        */}
        <CapabilityButton
          capability={capabilities?.enrollCleanup ?? DENIED_CAPABILITY}
          label="Atualizar agente para concluir a exclusão"
          busy={busy}
          onClick={pair('cleanup-enrollment')}
        />
      </Box>
      <Box className={classes.row}>
        <CapabilityButton
          capability={capabilities?.finalize ?? DENIED_CAPABILITY}
          label="Finalizar atividade"
          busy={busy}
          variant="contained"
          onClick={() => {
            void props.management.finalize();
          }}
        />
        <CapabilityButton
          capability={capabilities?.cancel ?? DENIED_CAPABILITY}
          label="Cancelar atividade"
          busy={busy}
          color="secondary"
          onClick={() => setCancelOpen(true)}
        />
      </Box>
      <ControlPlaneUsageRecoveryPanel
        management={props.management}
        capability={capabilities?.recoverUsage ?? DENIED_CAPABILITY}
        activityTitle={props.activityTitle}
        busy={busy}
      />
      <PairingIntentDialog
        purpose={pairingIntent}
        busy={busy}
        onClose={() => setPairingIntent(null)}
        onConfirm={purpose => {
          setPairingIntent(null);
          void props.onboarding.generate(purpose);
        }}
      />
      <ResumeAttemptDialog
        open={resumeOpen}
        busy={busy}
        onClose={() => setResumeOpen(false)}
        onConfirm={() => {
          setResumeOpen(false);
          const sessionId = props.management.projection?.attempt?.sessionId;
          if (sessionId) void props.onboarding.resume(sessionId);
        }}
      />
      <ControlPlaneCancelDialog
        open={cancelOpen}
        activityTitle={props.activityTitle}
        busy={busy}
        onClose={() => setCancelOpen(false)}
        onConfirm={reason => {
          setCancelOpen(false);
          void props.management.cancel(reason);
        }}
      />
    </Box>
  );
}

function ActivityConnectionPage({ activityId }: { activityId: string }) {
  const classes = useStyles();
  const view = useControlPlaneOnboarding(activityId);
  const management = useControlPlaneManagement(activityId);
  const skillPlan = useControlPlaneSkillPlan(activityId);
  const navigate = useNavigate();
  const activityCockpitRoute = useRouteRef(activityCockpitRouteRef);
  // Owner-scoped onboarding state first, then the ACL-scoped management
  // projection. A read-only participant gets 403 from the owner endpoint, so the
  // projection is what supplies their title (E2E-003); an owner sees both agree.
  let authorizedActivity =
    view.activity?.id === activityId ? view.activity : undefined;
  if (!authorizedActivity && management.activity?.id === activityId) {
    authorizedActivity = management.activity;
  }
  const authorizedTitle = authorizedActivity?.title.trim();
  // The read FAILED (as opposed to still loading): no projection and an error.
  // Nothing on the page may assert a connection state in that window. The notice
  // below is rendered only when the onboarding read succeeded — otherwise
  // ConnectionCard already owns the single error alert and a second one would
  // compete for the same announcement.
  const projectionUnavailable =
    !management.loading && !management.projection && management.error !== null;
  // NOTE: there is deliberately no effect that navigates away when the stored
  // handoff says "connected". That redirect was the reported defect: it made a
  // connected — or persisted-connected but expired — activity unreachable for
  // management. Only a newly confirmed local pairing navigates to the Cockpit,
  // and it does so from an explicit action, not from a render effect (ADR-002).
  return (
    <Page themeId="tool">
      <Header
        title={authorizedTitle || 'Control Plane'}
        subtitle="Gerencie a conexão desta atividade"
      />
      <Content>
        <Box className={classes.layout}>
          {projectionUnavailable && view.activity ? (
            <Card variant="outlined">
              <CardContent className={classes.section}>
                <Typography role="alert" color="error">
                  {management.error ??
                    'Não foi possível ler o estado operacional desta atividade.'}
                </Typography>
                <Typography variant="body2">
                  Nenhum estado é apresentado enquanto a leitura canônica
                  estiver indisponível. Recarregue para tentar novamente.
                </Typography>
              </CardContent>
            </Card>
          ) : null}
          {management.projection ? (
            <Card variant="outlined">
              <CardContent className={classes.section}>
                <ManagementStatePanel
                  projection={management.projection}
                  stale={management.stale}
                />
                <ManagementActions
                  management={management}
                  onboarding={view}
                  activityTitle={authorizedTitle || 'Atividade'}
                />
                {management.error ? (
                  <Typography role="alert" color="error">
                    {management.error}
                  </Typography>
                ) : null}
                <Box className={classes.row}>
                  <CapabilityButton
                    capability={
                      management.projection.capabilities.openCockpit ??
                      DENIED_CAPABILITY
                    }
                    label="Abrir Cockpit"
                    busy={false}
                    onClick={() =>
                      navigate(activityCockpitRoute({ activityId }))
                    }
                  />
                </Box>
                {/*
                  Announced live, but deliberately NOT role="status": the pairing
                  card below already owns the page's single status container, and
                  two role="status" regions on one page compete for the same
                  announcement slot. A labelled aria-live region keeps terminal
                  feedback distinguishable from pairing feedback.
                */}
                <Typography
                  aria-live="polite"
                  aria-atomic="true"
                  aria-label="Estado da gestão da atividade"
                >
                  {management.announcement}
                </Typography>
              </CardContent>
            </Card>
          ) : null}
          <ControlPlaneSkillPlanPanel view={skillPlan} />
          {view.activity || view.loading || !management.projection ? (
            <ConnectionCard
              view={view}
              pairCapability={management.projection?.capabilities.pair}
              announcement={
                view.announcement ||
                canonicalAnnouncement(management.projection)
              }
            />
          ) : null}
        </Box>
      </Content>
    </Page>
  );
}

export const ControlPlaneConnectPage = () => {
  const classes = useStyles();
  const { activityId } = useParams<{ activityId: string }>();
  if (activityId) {
    return <ActivityConnectionPage key={activityId} activityId={activityId} />;
  }
  return (
    <Page themeId="tool">
      <Header
        title="Control Plane"
        subtitle="Prepare o workspace local e conecte o agente"
      />
      <Content>
        <Box className={classes.layout}>
          <Typography role="alert">Atividade não informada.</Typography>
        </Box>
      </Content>
    </Page>
  );
};
